import React, { useEffect } from "react";

import { IgDialog, IgLink, IgTypography } from "@intelligems/ig-theme";
import { officialCategoryName } from "@intelligems/ig-utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useResetExperience } from "@src/api/experience-actions/reset-experience";
import { onPriceTestAction } from "@src/features/experiments/utils/experienceActionUtils";
import { useDialog } from "@src/hooks/dialog";
import { useDispatch } from "@src/store";
import { resetToPendingFaq } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

export const ConfirmExperienceResetDialog = () => {
  const dialog = useDialog("confirmReset");
  const category = dialog.props?.experience?.category ?? "experiment";
  const resetExperience = useResetExperience(category);
  const dispatch = useDispatch();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const onYesClick = () => {
    if (dialog.props?.experienceId && dialog.props?.experience) {
      // Check if this is a pricing test
      if (
        dialog.props.experience.testTypes?.hasTestPricing &&
        dialog.props.experience.status === "started"
      ) {
        // For pricing tests, show the PriceUpdaterDialog first
        onPriceTestAction(
          dialog.props.experience,
          "reset", // Using "reset" as the action type
          storeCurrencyConfig?.value?.code ?? "USD",
          dispatch
        );
        dialog.close();
      } else {
        // For non-pricing tests, proceed with direct reset
        resetExperience.mutate({
          body: { variationId: undefined },
          queryParams: undefined,
          pathParams: {
            experienceId: dialog.props.experienceId,
            action: "reset",
            name: dialog.props.experience.name,
          },
        });
        dialog.close();
      }
    }
  };

  useEffect(() => {
    if (resetExperience.isSuccess) {
      resetExperience.reset();
    }
  }, [resetExperience]);

  return (
    <IgDialog
      title={
        dialog.props?.experience?.name ||
        `Reset ${officialCategoryName(category, true)}`
      }
      subtitle={`Are you sure you want to reset this ${officialCategoryName(category, false)}? This will reset the ${officialCategoryName(category, false)} data.`}
      handleClose={dialog.close}
      showDialog={dialog.isOpen}
      handleYesClick={onYesClick}
      yesButtonText={`Yes, reset this ${officialCategoryName(category, false)}`}
      loading={resetExperience.isPending}
      shouldNotClose={true}
      dialogHeight={null}
      dialogContentWidth="100%"
      contentSx={{ padding: "8px 0 0 0" }}
      content={
        <IgTypography>
          Resetting clears all collected data and moves your test back to
          pending status. Your test configuration remains intact—only the
          results are removed. You can then relaunch the test to begin
          collecting fresh data.
          <br />
          <br />
          <IgLink
            onClick={() => {
              openNewTab(resetToPendingFaq);
            }}
          >
            {`Learn more about resetting ${officialCategoryName(category, false)}s.`}
          </IgLink>
        </IgTypography>
      }
    />
  );
};
